import { combineLatest, of, startWith, tap } from 'rxjs';
import { TuiCheckboxModule, TuiMultiSelectComponent } from '@taiga-ui/kit';
import { TuiOptionComponent, TuiTextfieldSizeDirective } from '@taiga-ui/core';
import { EMPTY_QUERY, tuiQueryListChanges } from '@taiga-ui/cdk';
import { FormsModule, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  forwardRef,
  inject,
  QueryList,
} from '@angular/core';
import { NgIf } from '@angular/common';

@Component({
  selector: 'iss-multiselect-all',
  templateUrl: './multiselect-all.component.html',
  styleUrls: [ './multiselect-all.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    TuiCheckboxModule,
  ],
})
export class MultiselectAllComponent<T> implements AfterViewInit {
  private readonly multiselect = inject<TuiMultiSelectComponent<T>>(TuiMultiSelectComponent);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngControl = inject(NgControl);
  private readonly sizeDirective = inject(TuiTextfieldSizeDirective, { optional: true });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  @ContentChildren(forwardRef(() => TuiOptionComponent), { descendants: true })
  private readonly options: QueryList<TuiOptionComponent<T>> = EMPTY_QUERY;

  ngAfterViewInit(): void {
    const value$ = this.ngControl.valueChanges?.pipe(startWith([])) ?? of([]);

    combineLatest([
      value$,
      tuiQueryListChanges(this.options),
    ]).pipe(
      tap(() => this.cdr.markForCheck()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public onChange(selected: boolean): void {
    const visibleOptions = this.getVisibleOptions();
    const selectedOptions = this.getSelectedOptions();
    if (selected) {
      visibleOptions.forEach(visibleOption => selectedOptions.add(visibleOption));
    } else {
      visibleOptions.forEach(visibleOption => selectedOptions.delete(visibleOption));
    }

    this.multiselect.value = [ ...selectedOptions.values() ];
  }

  public get isVisible(): boolean {
    return this.getVisibleOptions().size > 1;
  }

  public get selected(): boolean | null {
    const selectedOptions = this.getSelectedOptions();
    const visibleOptions = [ ...this.getVisibleOptions().values() ];
    if (visibleOptions.every(value => selectedOptions.has(value))) return true;
    if (visibleOptions.some(value => selectedOptions.has(value))) return null;
    return false;
  }

  public get small() {
    return this.sizeDirective?.size === 's';
  }

  private getVisibleOptions(): Set<T> {
    const options = this.options.map(option => option.value).filter(value => value != null) as readonly T[];
    return new Set(options);
  }

  private getSelectedOptions(): Set<T> {
    return new Set(this.multiselect.value);
  }
}
